import { useState } from "react";
import SectionHead from "@/components/common/SectionHead";
import Tilt from "@/components/common/Tilt";
import { ArrowRight } from "@/components/common/icons";
import { ACCOUNT_TIERS, fmtUSD } from "@/lib/data";
import { env } from "@/lib/env";
import { cx } from "@/lib/cx";
import { useLocale } from "@/i18n/useLocale";
import { chromeFor } from "@/content/chrome";
import { landingConfiguratorFor, evaluationTermsFor } from "@/content/landing";
import { formatPercent } from "@/lib/format";

type Model = "2step" | "1step";

// Order is fixed here; the button labels come from content.
const MODEL_IDS: readonly Model[] = ["2step", "1step"];

interface AccountSpec {
  fee: number;
  target: number;
  splitPct: number;
  ddDaily: number;
  ddMax: number;
}

// Drawdown rules vary by evaluation model, not by account size:
//   2-Step: 5% daily / 10% max — gentler, two-phase path
//   1-Step: 4% daily / 7% max  — tighter, single-phase path
// Both apply to the starting balance. The configurator computes
// the absolute USD numbers from `tier.size × pct` so the values
// update live as the user toggles the model — the static
// `tier.ddDaily` / `tier.ddMax` fields baked into ACCOUNT_TIERS
// are no longer load-bearing for this surface.
const MODEL_DD: Record<Model, { daily: number; max: number }> = {
  "2step": { daily: 0.05, max: 0.10 },
  "1step": { daily: 0.04, max: 0.07 },
};

function specFor(model: Model, tier: typeof ACCOUNT_TIERS[number]): AccountSpec {
  const dd = MODEL_DD[model];
  return {
    fee: model === "1step" ? tier.fee1Step : tier.fee2Step,
    target: model === "1step" ? tier.target1Step : tier.target2Step,
    splitPct: tier.splitPct,
    ddDaily: tier.size * dd.daily,
    ddMax: tier.size * dd.max,
  };
}

function sizeLabel(size: number): string {
  return `$${size >= 1000 ? `${size / 1000}K` : size}`;
}

// Phase RW.13 (2026-05-07): rebuilt to match the mockup composition.
// Old layout was a 3-column [model picker | size chips + rules |
// ticket]. New layout is the mockup's 2-column [slider card with
// model toggle + huge size + dot slider + drawdown stat tiles |
// brand-glow ticket card with spec rows + YOU PAY total]. The
// existing accessibility contracts hold: every size dot is a
// `<button>` whose accessible name is the size label ("$10K",
// "$100K" — pinned by `Landing.test.tsx`); the model toggle has a
// `<button>` with `aria-pressed="true"` by default (also pinned).
export default function AccountSelector(): JSX.Element {
  const [model, setModel] = useState<Model>("2step");
  const [sizeIdx, setSizeIdx] = useState(3); // $100K default — matches both the mockup's slider default and the previous configurator default; also keeps the hero ticket's $50,000 unique on the Landing page so the `Landing.test.tsx::renders the hero preview ticket` pin resolves to a single match.
  const locale = useLocale();
  const t = landingConfiguratorFor(locale);
  const terms = evaluationTermsFor(locale);
  const chrome = chromeFor(locale);
  const tier = ACCOUNT_TIERS[sizeIdx]!;
  const spec = specFor(model, tier);
  const fillPct = (sizeIdx / (ACCOUNT_TIERS.length - 1)) * 100;

  return (
    <section id="configure" className="section-y-lg relative overflow-hidden">
      <div
        className="aura-bg"
        aria-hidden="true"
        style={{ ["--aura-size" as string]: "500px", ["--aura-top" as string]: "-200px", ["--aura-opacity" as string]: "0.4" }}
      />
      <div className="container-page relative z-[2]">
        <SectionHead
          align="center"
          title={
            <>
              {t.title.lead}
              <span className="text-brand-gradient">{t.title.highlight}</span>
            </>
          }
          sub={t.sub}
        />

        <div className="mt-10 md:mt-12 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          {/* LEFT — slider card */}
          <div className="glass-card p-5 md:p-9">
            {/* Model segmented control */}
            <div
              className="inline-flex gap-1 p-1 rounded-[10px] mb-5 md:mb-8"
              style={{ background: "rgba(0, 0, 0, 0.4)" }}
            >
              {MODEL_IDS.map((id) => {
                const active = id === model;
                const label = id === "2step" ? t.models.twoStep : t.models.oneStep;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setModel(id)}
                    aria-pressed={active}
                    className={cx(
                      "px-4 md:px-5 py-2 md:py-2.5 rounded-lg border-none cursor-pointer text-[11px] md:text-[12px] font-semibold tracking-[0.04em] transition-all",
                      active ? "text-white" : "text-fg-muted hover:text-fg",
                    )}
                    style={
                      active
                        ? {
                            background:
                              "linear-gradient(180deg, rgb(var(--brand)) 0%, color-mix(in srgb, rgb(var(--brand)) 78%, black) 100%)",
                            boxShadow: "0 8px 20px -6px rgb(var(--brand) / 0.5)",
                          }
                        : { background: "transparent" }
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Account size — huge gradient display */}
            <div className="font-mono text-[11px] tracking-[0.2em] text-fg-dim uppercase">{t.accountSize}</div>
            <div
              className="font-display font-bold text-brand-gradient mt-1 md:mt-2 leading-none"
              style={{ fontSize: "clamp(44px, 11vw, 96px)", letterSpacing: "-0.04em" }}
            >
              {fmtUSD(tier.size, locale)}
            </div>

            {/* Slider — 5 dots on a track */}
            <div className="cfg-slider relative mt-6 md:mt-9 flex items-center" style={{ height: 50 }}>
              <div
                className="cfg-slider-track absolute left-0 right-0 rounded-full"
                style={{ height: 2, background: "rgba(255, 255, 255, 0.08)" }}
              />
              <div
                className="cfg-slider-fill absolute left-0 rounded-full"
                style={{
                  height: 2,
                  width: `${fillPct}%`,
                  background:
                    "linear-gradient(90deg, rgb(var(--brand)), color-mix(in srgb, rgb(var(--brand)) 50%, transparent))",
                  boxShadow: "0 0 12px rgb(var(--brand))",
                }}
              />
              {ACCOUNT_TIERS.map((t, i) => {
                const active = i === sizeIdx;
                return (
                  <button
                    key={t.size}
                    type="button"
                    onClick={() => setSizeIdx(i)}
                    aria-pressed={active}
                    className="flex-1 bg-transparent border-none cursor-pointer relative z-[1] py-3"
                  >
                    <span
                      aria-hidden="true"
                      className="block mx-auto rounded-full transition-all"
                      style={{
                        width: 16,
                        height: 16,
                        background: active ? "rgb(var(--brand))" : "rgb(var(--bg-2))",
                        border: `2px solid ${active ? "rgb(var(--brand))" : "rgba(255, 255, 255, 0.18)"}`,
                        boxShadow: active
                          ? "0 0 16px rgb(var(--brand)), 0 0 0 4px rgb(var(--brand) / 0.2)"
                          : "none",
                      }}
                    />
                    <span
                      className={cx(
                        "block font-mono mt-2.5 tracking-[0.1em]",
                        active ? "text-fg" : "text-fg-dim",
                      )}
                      style={{ fontSize: 10 }}
                    >
                      {sizeLabel(t.size)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Drawdown stat tiles — values flip with the model toggle */}
            <div className="grid grid-cols-2 gap-3 mt-5 md:mt-8">
              {([
                [t.dailyDrawdown, fmtUSD(spec.ddDaily, locale)],
                [t.maxDrawdown, fmtUSD(spec.ddMax, locale)],
              ] as const).map(([k, v]) => (
                <div
                  key={k}
                  className="rounded-[10px] border border-border px-3.5 py-2.5 md:px-[18px] md:py-[16px]"
                  style={{ background: "rgba(0, 0, 0, 0.3)" }}
                >
                  <div className="font-mono text-[9px] tracking-[0.2em] text-fg-dim uppercase">{k}</div>
                  <div
                    className="font-display font-bold text-fg mt-1 leading-none text-[18px] md:text-[22px]"
                    style={{ letterSpacing: "-0.02em" }}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — featured ticket card with brand glow */}
          <Tilt max={3} scale={1.005} className="h-full">
          <div
            className="glass-card glass-card-glow p-5 md:p-8 flex flex-col h-full"
            style={{
              background:
                "linear-gradient(180deg, rgb(var(--brand) / 0.10) 0%, rgb(var(--brand) / 0.02) 100%)",
            }}
          >
            <div className="font-mono text-[9px] tracking-[0.2em] text-fg-dim uppercase mb-1">
              {t.ticketLabel} · #AF-2241
            </div>
            <div className="font-mono text-[12px] flex flex-col flex-1">
              {([
                [terms.rows.oneTimeFee, fmtUSD(spec.fee, locale)],
                [terms.rows.profitTarget, fmtUSD(spec.target, locale)],
                [terms.rows.profitSplit, formatPercent(spec.splitPct, locale)],
                [terms.rows.leverage, "1:100"],
                [terms.rows.timeLimit, terms.values.noTimeLimit],
              ] as const).map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between items-baseline py-2.5 md:py-3.5"
                  style={{ borderBottom: "1px dashed rgb(var(--border))" }}
                >
                  <span className="text-fg-muted tracking-[0.06em]">{k}</span>
                  <span className="text-fg font-bold">{v}</span>
                </div>
              ))}
              <div className="flex justify-between items-baseline pt-4 md:pt-5">
                <span className="text-fg-muted tracking-[0.06em]">{t.youPay}</span>
                <span
                  className="font-display font-bold text-brand-gradient leading-none text-[28px] md:text-[36px]"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  {fmtUSD(spec.fee, locale)}
                </span>
              </div>
            </div>
            <a
              href={env.registerUrl}
              className="btn btn-primary w-full justify-center mt-5 md:mt-6"
            >
              {chrome.cta.getFunded} <ArrowRight />
            </a>
          </div>
          </Tilt>
        </div>
      </div>
    </section>
  );
}
